import { useIdleTimer } from "react-idle-timer";
import { User } from "./types";
import { enqueueSnackbar } from "notistack";
import { useContext } from "react";
import { context } from "./Provider";

/**
 * Enum for predefined idle timeout durations.
 * @enum {number}
 */
export enum IdleTimeoutDuration {
  /** 10 seconds timeout (for testing) */
  TEN_SECONDS = 10 * 1000,
  /** 1 hour timeout */
  ONE_HOUR = 1 * 60 * 60 * 1000,
  /** 2 hours timeout */
  TWO_HOURS = 2 * 60 * 60 * 1000,
  /** 3 hours timeout */
  THREE_HOURS = 3 * 60 * 60 * 1000,
  /** 4 hours timeout */
  FOUR_HOURS = 4 * 60 * 60 * 1000,
}

/**
 * Configuration options for the useIdleLogout hook.
 * @interface IdleLogoutConfig
 */
interface IdleLogoutConfig {
  /** The predefined idle timeout duration or custom milliseconds. Defaults to TEN_SECONDS. */
  timeout?: IdleTimeoutDuration | number;
  /** The duration in milliseconds to display the snackbar notification. If null, the snackbar persists. */
  snackbarDuration?: number | null;
}

/**
 * Default idle timeout duration set to 10 seconds for testing.
 * @constant {IdleTimeoutDuration}
 */
const DEFAULT_IDLE_TIMEOUT = IdleTimeoutDuration.TWO_HOURS;

/**
 * A hook that logs out a user after a period of inactivity.
 * @param {User | undefined} user - The current user object, or undefined if no user is logged in.
 * @param {() => void} logOut - Function to execute when logging out the user.
 * @param {IdleLogoutConfig} [config={}] - Configuration options for idle timeout and snackbar duration.
 * @returns {{ reset: () => void }} - An object containing a reset function to restart the idle timer.
 */
export function useIdleLogout(user: User | undefined, logOut: () => void, config: IdleLogoutConfig = {}) {
  const { timeout = DEFAULT_IDLE_TIMEOUT, snackbarDuration = null } = config;
  const { setUser, setToken } = useContext(context);

  const { reset } = useIdleTimer({
    timeout,
    onIdle: () => {
      if (user) {
        enqueueSnackbar("Your session has expired due to inactivity. You have been logged out.", {
          variant: "warning",
          autoHideDuration: snackbarDuration,
        });

        setTimeout(() => {
          logOut();
          setUser(undefined);
          setToken(undefined);
        }, snackbarDuration || 0);
      }
    },
    events: [
      "mousemove",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
      "visibilitychange",
    ],
    debounce: 500,
  });

  return { reset };
}
